import { toast } from "@/hooks/use-toast";

export interface ExportData {
  [key: string]: any;
}

export interface ExportOptions {
  filename?: string;
  headers?: string[];
  dataMapper?: (item: any) => any[];
  toast?: {
    success?: string;
    error?: string;
  };
}

/**
 * Generic CSV export utility function
 * @param data - Array of data objects to export
 * @param options - Export configuration options
 *
 * @example
 * // Basic usage with automatic headers
 * exportToCSV(myData);
 *
 * @example
 * // Custom headers and filename
 * exportToCSV(myData, {
 *   filename: "custom_export",
 *   headers: ["Name", "Email", "Status"],
 *   dataMapper: (item) => [item.name, item.email, item.status]
 * });
 */
export const exportToCSV = (
  data: ExportData[],
  options: ExportOptions = {}
) => {
  const {
    filename = `export_${new Date().toISOString().split('T')[0]}`,
    headers,
    dataMapper,
    toast: toastOptions = {}
  } = options;

  // Check if there's data to export
  if (!data || data.length === 0) {
    const errorMessage = toastOptions.error || "No data to export";
    toast({ title: errorMessage, variant: "destructive" });
    return;
  }

  // If no headers provided, use object keys from first item
  const exportHeaders = headers || (data[0] ? Object.keys(data[0]) : []);

  // If no custom mapper, use default mapping
  const mapper = dataMapper || ((item: any) => exportHeaders.map(key => item[key] || ""));

  // Create CSV content
  const csvContent = [
    exportHeaders.join(","),
    ...data.map(item => mapper(item).map(field => `"${field}"`).join(","))
  ].join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Show success message
  const successMessage = toastOptions.success || "Data exported successfully";
  toast({ title: successMessage });
};

/**
 * Specific utility for exporting employee data
 * @param employees - Array of employee objects
 * @param filename - Optional custom filename
 *
 * @example
 * // Basic usage
 * exportEmployeesToCSV(employees);
 *
 * @example
 * // Custom filename
 * exportEmployeesToCSV(employees, "my_company_employees");
 */
export const exportEmployeesToCSV = (
  employees: any[],
  filename?: string
) => {
  const headers = [
    "First Name",
    "Last Name",
    "Email",
    "Phone",
    "Department",
    "Position",
    "Status",
    "Hire Date",
    "Salary"
  ];

  const dataMapper = (emp: any) => [
    emp.firstName,
    emp.lastName,
    emp.email,
    emp.phone || "",
    emp.department || "",
    emp.position || "",
    emp.status,
    emp.hireDate || "",
    emp.salary || ""
  ];

  exportToCSV(employees, {
    filename: filename || `employees_${new Date().toISOString().split('T')[0]}`,
    headers,
    dataMapper,
    toast: {
      success: "Employees exported successfully",
      error: "No employees to export"
    }
  });
};

// ---------------------------------------------------------------------------
// Employee export presets (Phase 10) — used by the Export dialog on the client
// Employees tab and the agency Employees tab. Each preset is a named column
// set; "value" reads from the roster row the tables already load.
// ---------------------------------------------------------------------------

export interface EmployeeExportField {
  header: string;
  value: (emp: any) => any;
}

export interface EmployeeExportPreset {
  key: string;
  label: string;
  description: string;
  fields: EmployeeExportField[];
  /** Append dependent1..5 column groups (requires dependents on each row). */
  includeDependents?: boolean;
}

const field = (header: string, key: string): EmployeeExportField => ({
  header,
  value: (emp) => emp?.[key] ?? "",
});

const BASIC_FIELDS: EmployeeExportField[] = [
  field("First Name", "firstName"),
  field("Last Name", "lastName"),
  field("Email", "email"),
  field("Phone", "phone"),
  field("Status", "status"),
  field("Department", "department"),
  field("Position", "position"),
  field("Hire Date", "hireDate"),
];

const DETAILED_FIELDS: EmployeeExportField[] = [
  field("id", "id"),
  field("firstName", "firstName"),
  field("lastName", "lastName"),
  field("email", "email"),
  field("phone", "phone"),
  field("status", "status"),
  field("hireDate", "hireDate"),
  field("terminationDate", "terminationDate"),
  field("department", "department"),
  field("position", "position"),
  field("employmentType", "employmentType"),
  field("employeeId", "employeeId"),
  field("scheduledHours", "scheduledHours"),
  field("compensationType", "compensationType"),
  field("compensationAmount", "compensationAmount"),
  field("payCycle", "payCycle"),
  field("salary", "salary"),
  field("hourlyRate", "hourlyRate"),
  field("contractType", "contractType"),
  field("contractStartDate", "contractStartDate"),
  field("contractEndDate", "contractEndDate"),
  field("managerEmployeeId", "managerEmployeeId"),
  field("performanceRating", "performanceRating"),
  field("lastReviewDate", "lastReviewDate"),
  field("skills", "skills"),
  field("address1", "address1"),
  field("address2", "address2"),
  field("city", "city"),
  field("state", "state"),
  field("zipCode", "zipCode"),
  field("county", "county"),
  field("country", "country"),
  field("emergencyContact", "emergencyContact"),
  field("emergencyPhone", "emergencyPhone"),
];

const CENSUS_FIELDS: EmployeeExportField[] = [
  field("firstName", "firstName"),
  field("lastName", "lastName"),
  field("dateOfBirth", "dateOfBirth"),
  field("sex", "sex"),
  field("maritalStatus", "maritalStatus"),
  field("ssn", "ssn"),
  field("tobaccoUser", "tobaccoUser"),
  field("disabled", "disabled"),
  field("zipCode", "zipCode"),
  field("county", "county"),
  field("state", "state"),
  field("employmentType", "employmentType"),
  field("scheduledHours", "scheduledHours"),
  field("salary", "salary"),
  field("hireDate", "hireDate"),
  field("status", "status"),
];

const FULL_FIELDS: EmployeeExportField[] = [
  ...DETAILED_FIELDS,
  field("dateOfBirth", "dateOfBirth"),
  field("sex", "sex"),
  field("maritalStatus", "maritalStatus"),
  field("ssn", "ssn"),
  field("tobaccoUser", "tobaccoUser"),
  field("disabled", "disabled"),
  field("taxId", "taxId"),
  field("locationId", "locationId"),
  field("employeeClassId", "employeeClassId"),
  field("divisionId", "divisionId"),
];

export const EMPLOYEE_EXPORT_PRESETS: EmployeeExportPreset[] = [
  {
    key: "basic",
    label: "Basic Employee Report",
    description: "Name, contact, status and position — a quick roster.",
    fields: BASIC_FIELDS,
  },
  {
    key: "detailed",
    label: "Detailed Employment Export",
    description: "Full employment, compensation and contact fields.",
    fields: DETAILED_FIELDS,
  },
  {
    key: "census",
    label: "Census Export",
    description:
      "Benefits-oriented: DOB, sex, SSN, ZIP and dependents — for carrier quotes.",
    fields: CENSUS_FIELDS,
    includeDependents: true,
  },
  {
    key: "full",
    label: "Full Export (everything)",
    description: "Every field plus dependents — the previous Export behavior.",
    fields: FULL_FIELDS,
    includeDependents: true,
  },
];

const DEPENDENT_SUBFIELDS = [
  "firstName",
  "lastName",
  "relationship",
  "dateOfBirth",
  "ssn",
  "sex",
] as const;

/**
 * Export employees using a preset column set. Rows must carry `dependents`
 * arrays when the preset includes dependent columns.
 */
export const exportEmployeesWithPreset = (
  employees: any[],
  preset: EmployeeExportPreset,
  filename: string,
) => {
  const headers = preset.fields.map((f) => f.header);
  if (preset.includeDependents) {
    for (let i = 1; i <= 5; i++) {
      for (const sub of DEPENDENT_SUBFIELDS) headers.push(`dependent${i}_${sub}`);
    }
  }
  const dataMapper = (emp: any) => {
    const row = preset.fields.map((f) => f.value(emp) ?? "");
    if (preset.includeDependents) {
      const dependents = emp?.dependents || [];
      for (let i = 1; i <= 5; i++) {
        const dep = dependents[i - 1];
        for (const sub of DEPENDENT_SUBFIELDS) row.push(dep?.[sub] || "");
      }
    }
    return row;
  };
  exportToCSV(employees, {
    filename,
    headers,
    dataMapper,
    toast: { success: "Employees exported successfully" },
  });
};

/**
 * Download an employee import CSV template with all required fields
 * @param filename - Optional custom filename
 *
 * @example
 * downloadEmployeeImportTemplate();
 */
export const downloadEmployeeImportTemplate = (filename?: string) => {
  const headers = [
    "employeeId",
    "firstName",
    "lastName",
    "email",
    "personalEmail",
    "phone",
    "dateOfBirth",
    "sex",
    "maritalStatus",
    "ssn",
    "tobaccoUser",
    "disabled",
    "address1",
    "address2",
    "city",
    "state",
    "zipCode",
    "county",
    "country",
    "department",
    "position",
    "location",
    "employeeClass",
    "division",
    "employmentType",
    "hireDate",
    "salary",
    "status",
    "scheduledHours",
    "compensationType",
    "compensationAmount",
    "payCycle",
    "emergencyContact",
    "emergencyPhone"
  ];

  // Add dependent columns (up to 5 dependents)
  for (let i = 1; i <= 5; i++) {
    headers.push(
      `dependent${i}_firstName`,
      `dependent${i}_lastName`,
      `dependent${i}_relationship`,
      `dependent${i}_dateOfBirth`,
      `dependent${i}_ssn`,
      `dependent${i}_sex`
    );
  }

  // Example row to show the expected format for each column.
  // The "EXAMPLE" value in employeeId marks this as a sample — the importer
  // skips any row tagged this way, so it is safe to leave it in the file.
  // Dates MUST be YYYY-MM-DD (or M/D/YYYY); other formats are rejected on upload.
  const exampleValues: Record<string, string> = {
    employeeId: "EXAMPLE (delete this row or leave it — it is ignored on import)",
    firstName: "Jane",
    lastName: "Doe",
    email: "jane.doe@example.com",
    personalEmail: "jane.personal@example.com",
    phone: "555-123-4567",
    dateOfBirth: "1985-03-15",
    sex: "F",
    maritalStatus: "Married",
    ssn: "123-45-6789",
    tobaccoUser: "No",
    disabled: "No",
    address1: "123 Main St",
    address2: "Apt 2B",
    city: "Austin",
    state: "TX",
    zipCode: "78701",
    county: "Travis",
    country: "United States",
    department: "Sales",
    position: "Account Manager",
    location: "Austin HQ",
    employeeClass: "Salaried",
    division: "Southwest",
    employmentType: "Full-time",
    hireDate: "2020-06-01",
    salary: "75000",
    status: "Active",
    scheduledHours: "40",
    compensationType: "Salary",
    compensationAmount: "75000",
    payCycle: "Bi-weekly",
    emergencyContact: "John Doe",
    emergencyPhone: "555-987-6543",
    dependent1_firstName: "John",
    dependent1_lastName: "Doe",
    dependent1_relationship: "Spouse",
    dependent1_dateOfBirth: "1984-11-20",
    dependent1_ssn: "987-65-4321",
    dependent1_sex: "M",
  };

  // Create CSV content: quoted header row + a quoted example row
  const headerLine = headers.map(header => `"${header}"`).join(",");
  const exampleLine = headers
    .map(header => `"${exampleValues[header] ?? ""}"`)
    .join(",");
  const csvContent = `${headerLine}\n${exampleLine}`;

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename || "employee_import_template.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Show success message
  toast({ title: "Template downloaded successfully", variant: "success" });
};
